'use client';

import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import DemoDataPill from './DemoDataPill';

const cardStyle = {
  background: '#FFFFFF',
  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  border: '1px solid #F0F0F0',
};

const DAY_MS = 86_400_000;
// All date-based urgency colors are computed against the user's local "today"
// at UTC midnight, so the widget reads consistently across timezones.
const todayUtcMs = new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00Z').getTime();
const daysFrom = (isoYmd: string) => Math.floor((new Date(isoYmd + 'T00:00:00Z').getTime() - todayUtcMs) / DAY_MS);
const daysSince = (isoYmd: string) => -daysFrom(isoYmd);

export default function Row8ExternalAndRisk() {
  return (
    <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <InvestorUpdateStatus />
      <PressMentions />
      <ComplianceCalendar />
      <HeadcountByDepartment />
    </section>
  );
}

// ── 1. Investor Update Status ──────────────────────────────────────────────

const INVESTOR = {
  lastSentIso: '',
  recipientCount: 0,
  drafts: [] as { topic: string; owner: string; daysAged: number }[],
};

function investorPill(days: number): { color: string; bg: string } {
  if (days > 45) return { color: '#B91C1C', bg: 'rgba(239,68,68,0.10)' };
  if (days > 30) return { color: '#92400E', bg: 'rgba(245,158,11,0.10)' };
  return                { color: '#047857', bg: 'rgba(16,185,129,0.10)' };
}

function InvestorUpdateStatus() {
  // Guard: empty lastSentIso would produce NaN days and an "Invalid Date" label.
  const hasLastSent = !!INVESTOR.lastSentIso;
  const days = hasLastSent ? daysSince(INVESTOR.lastSentIso) : 0;
  const pill = investorPill(days);
  const lastSentLabel = hasLastSent
    ? new Date(INVESTOR.lastSentIso + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-start justify-between gap-2 mb-3 pr-16">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name="EnvelopeIcon" size={15} style={{ color: '#033494' }} />
            <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
              Investor Updates
            </h3>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            <span
              className="rounded-full px-1.5 py-0.5 font-bold"
              style={{ background: pill.bg, color: pill.color }}
            >
              {days}d since last sent
            </span>
          </p>
        </div>
      </div>

      <div className="rounded-lg px-2.5 py-2 mb-3" style={{ border: '1px solid #F3F4F6' }}>
        <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">Last update sent</p>
        <p className="text-sm font-semibold text-gray-800 mt-0.5">{lastSentLabel}</p>
        <p className="text-[11px] text-gray-500">{INVESTOR.recipientCount} investors</p>
      </div>

      <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 mb-1.5">
        Drafts in progress
      </p>
      <div className="space-y-1">
        {INVESTOR.drafts.length === 0 ? (
          <p className="text-[11px] text-gray-400">No drafts</p>
        ) : (
          INVESTOR.drafts.map((d, i) => (
            <div key={i} className="flex items-center justify-between gap-2 text-[11px]">
              <span className="text-gray-700 truncate">
                {d.topic} <span className="text-gray-400">· {d.owner}</span>
              </span>
              <span className="text-gray-500 flex-shrink-0">{d.daysAged}d aged</span>
            </div>
          ))
        )}
      </div>

      <a
        href="#"
        className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold rounded-lg px-2.5 py-1.5 hover:opacity-90 active:scale-95 transition-all"
        style={{ background: 'rgba(3,52,148,0.10)', color: '#033494' }}
      >
        Send update
        <Icon name="ArrowRightIcon" size={10} />
      </a>
    </div>
  );
}

// ── 2. Press & PR Mentions ─────────────────────────────────────────────────

type Sentiment = 'Positive' | 'Neutral' | 'Negative';

interface Mention {
  source: string;
  headline: string;
  sentiment: Sentiment;
  dateLabel: string;
}

const MENTIONS: Mention[] = [];

function sentimentPill(s: Sentiment): { color: string; bg: string } {
  if (s === 'Positive') return { color: '#047857', bg: 'rgba(16,185,129,0.10)' };
  if (s === 'Negative') return { color: '#B91C1C', bg: 'rgba(239,68,68,0.10)' };
  return                       { color: '#6B7280', bg: 'rgba(107,114,128,0.10)' };
}

function PressMentions() {
  const monthCount = MENTIONS.filter((m) => m.dateLabel.startsWith('May') || m.dateLabel.startsWith('Apr')).length;
  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-start justify-between gap-2 mb-3 pr-16">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name="NewspaperIcon" size={15} style={{ color: '#07B0E9' }} />
            <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
              Press & PR
            </h3>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">{monthCount} mentions this month</p>
        </div>
      </div>
      <div className="space-y-2">
        {MENTIONS.map((m, i) => {
          const sp = sentimentPill(m.sentiment);
          return (
            <div key={i} className="rounded-lg px-2.5 py-2" style={{ border: '1px solid #F3F4F6' }}>
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 truncate">{m.source}</p>
                <span className="text-[10px] text-gray-400 flex-shrink-0">{m.dateLabel}</span>
              </div>
              <p className="text-[11px] text-gray-700 leading-snug mb-1 line-clamp-2">{m.headline}</p>
              <span
                className="rounded-full px-2 py-0.5 text-[9px] font-bold"
                style={{ background: sp.bg, color: sp.color }}
              >
                {m.sentiment}
              </span>
            </div>
          );
        })}
      </div>
      <a
        href="#"
        className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold rounded-lg px-2.5 py-1.5 hover:opacity-90 active:scale-95 transition-all"
        style={{ background: 'rgba(7,176,233,0.10)', color: '#0369A1' }}
      >
        View all mentions
        <Icon name="ArrowRightIcon" size={10} />
      </a>
    </div>
  );
}

// ── 3. Compliance Calendar ─────────────────────────────────────────────────

type ComplianceCategory = 'license' | 'tax' | 'insurance' | 'bond' | 'safety';

interface ComplianceItem {
  category: ComplianceCategory;
  name: string;
  jurisdiction: string;
  dueIso: string;
}

const COMPLIANCE_ITEMS: ComplianceItem[] = [];

function complianceIcon(c: ComplianceCategory): { name: string; color: string } {
  if (c === 'license')   return { name: 'IdentificationIcon',  color: '#033494' };
  if (c === 'tax')       return { name: 'BanknotesIcon',       color: '#F59E0B' };
  if (c === 'insurance') return { name: 'ShieldCheckIcon',     color: '#10B981' };
  if (c === 'bond')      return { name: 'DocumentCheckIcon',   color: '#8B5CF6' };
  return                       { name: 'ExclamationTriangleIcon', color: '#EF4444' }; // safety
}

function urgencyPill(daysUntil: number): { label: string; color: string; bg: string } {
  if (daysUntil < 0)   return { label: `Overdue ${Math.abs(daysUntil)}d`, color: '#B91C1C', bg: 'rgba(239,68,68,0.15)' };
  if (daysUntil < 14)  return { label: `${daysUntil}d`,                    color: '#B91C1C', bg: 'rgba(239,68,68,0.10)' };
  if (daysUntil <= 30) return { label: `${daysUntil}d`,                    color: '#92400E', bg: 'rgba(245,158,11,0.10)' };
  return                       { label: `${daysUntil}d`,                    color: '#047857', bg: 'rgba(16,185,129,0.10)' };
}

function ComplianceCalendar() {
  const sorted = [...COMPLIANCE_ITEMS]
    .map((c) => ({ ...c, daysUntil: daysFrom(c.dueIso) }))
    .sort((a, b) => a.daysUntil - b.daysUntil);
  const dueInNext60d = sorted.filter((c) => c.daysUntil >= 0 && c.daysUntil <= 60).length;
  const overdueCount = sorted.filter((c) => c.daysUntil < 0).length;

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-start justify-between gap-2 mb-3 pr-16">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name="ClipboardDocumentCheckIcon" size={15} style={{ color: '#F59E0B' }} />
            <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
              Compliance Calendar
            </h3>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">{dueInNext60d} due in next 60d</p>
        </div>
      </div>
      {overdueCount > 0 && (
        <div
          className="rounded-lg px-2.5 py-1.5 mb-2 text-[10px] font-bold uppercase tracking-wider"
          style={{ background: 'rgba(239,68,68,0.10)', color: '#B91C1C', border: '1px solid rgba(239,68,68,0.25)' }}
        >
          {overdueCount} overdue
        </div>
      )}
      <div className="space-y-2">
        {sorted.map((c, i) => {
          const ic = complianceIcon(c.category);
          const u = urgencyPill(c.daysUntil);
          return (
            <div key={i} className="flex items-start gap-2 rounded-lg px-2.5 py-2" style={{ border: '1px solid #F3F4F6' }}>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#F8F9FA' }}>
                <Icon name={ic.name} size={13} style={{ color: ic.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold text-gray-800 truncate">{c.name}</p>
                <p className="text-[10px] text-gray-500 truncate">{c.jurisdiction} · {new Date(c.dueIso + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
              </div>
              <span
                className="rounded-full px-2 py-0.5 text-[9px] font-bold flex-shrink-0"
                style={{ background: u.bg, color: u.color }}
              >
                {u.label}
              </span>
            </div>
          );
        })}
      </div>
      <a
        href="#"
        className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold rounded-lg px-2.5 py-1.5 hover:opacity-90 active:scale-95 transition-all"
        style={{ background: 'rgba(245,158,11,0.10)', color: '#92400E' }}
      >
        View compliance dashboard
        <Icon name="ArrowRightIcon" size={10} />
      </a>
    </div>
  );
}

// ── 4. Headcount by Department ─────────────────────────────────────────────

const HEADCOUNT: { dept: string; count: number; deltaQ: number; color: string }[] = [];

function HeadcountByDepartment() {
  const router = useRouter();
  const total = HEADCOUNT.reduce((s, h) => s + h.count, 0);

  return (
    <div className="relative rounded-xl p-5" style={cardStyle}>
      <DemoDataPill />
      <div className="flex items-start justify-between gap-2 mb-3 pr-16">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name="UserGroupIcon" size={15} style={{ color: '#10B981' }} />
            <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
              Headcount
            </h3>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">{total} total employees</p>
        </div>
      </div>
      <div className="flex h-4 rounded-full overflow-hidden mb-2" style={{ background: '#F3F4F6' }}>
        {HEADCOUNT.map((h) => (
          <div
            key={h.dept}
            style={{ width: `${(h.count / total) * 100}%`, background: h.color }}
            title={`${h.dept}: ${h.count}`}
          />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 mb-3">
        {HEADCOUNT.map((h) => (
          <div key={h.dept} className="flex items-center justify-between text-[11px]">
            <span className="flex items-center gap-1.5 min-w-0">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: h.color }} />
              <span className="text-gray-700 truncate">{h.dept}</span>
            </span>
            <span className="font-bold text-gray-700 flex-shrink-0">
              {h.count}
              {h.deltaQ !== 0 && (
                <span className="ml-1 font-semibold" style={{ color: h.deltaQ > 0 ? '#10B981' : '#EF4444' }}>
                  {h.deltaQ > 0 ? '+' : ''}{h.deltaQ}
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
      <div
        className="rounded-lg px-2.5 py-1.5 mb-2 text-[10px] font-semibold"
        style={{ background: 'rgba(7,176,233,0.08)', color: '#0369A1' }}
      >
        Hiring 4 more by Q3
      </div>
      <button
        onClick={() => router.push('/employee-directory')}
        className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold rounded-lg px-2.5 py-1.5 hover:opacity-90 active:scale-95 transition-all"
        style={{ background: 'rgba(16,185,129,0.10)', color: '#047857' }}
      >
        Open employee directory
        <Icon name="ArrowRightIcon" size={10} />
      </button>
    </div>
  );
}
